/**
 * The "Start now" hand-off: a prompt a customer pastes into their coding
 * agent to add Aimdoc identity verification to their MCP server, so services
 * can later run for their customers. The claims here mirror
 * backend/api/services/mcp_assertion_service.py (documented contract; keep
 * them in sync). The secret is never in the prompt: the agent reads it from
 * an environment variable and the user fetches it from Integrations.
 */

export type CodingAgentKey = 'claude' | 'codex' | 'cursor';

export interface CodingAgent {
  key: CodingAgentKey;
  name: string;
  /** What the user does after the click. */
  hint: string;
  /** Optional deep link that opens the agent with the prompt pre-filled. */
  deepLink?: (prompt: string) => string;
}

export const CODING_AGENTS: CodingAgent[] = [
  { key: 'claude', name: 'Claude Code', hint: 'Prompt copied. Paste it into Claude Code in your MCP server repo.' },
  { key: 'codex', name: 'Codex', hint: 'Prompt copied. Paste it into Codex in your MCP server repo.' },
  {
    key: 'cursor',
    name: 'Cursor',
    hint: 'Prompt copied and Cursor opened. If Cursor did not open, paste the prompt into its agent.',
    deepLink: (prompt) => `cursor://anysphere.cursor-deeplink/prompt?text=${encodeURIComponent(prompt)}`,
  },
];

export function buildIdentityPrompt({ mcpUrl, productName }: { mcpUrl: string; productName: string }): string {
  const url = mcpUrl.trim() || 'https://<your MCP server URL>';
  const name = productName.trim() || 'our product';
  return `Add support for Aimdoc identity assertions to the MCP server for ${name} at ${url}, alongside whatever authentication it already has. Aimdoc is the customer agent platform we use; when it calls our MCP server on behalf of one of our customers it sends a signed identity token so we can act as that customer.

How the token arrives
- Authorization: Bearer <JWT> on every MCP request (initialize, tools/list, tools/call).
- HS256, signed with our Aimdoc identity-verification secret. Read it from the environment variable AIMDOC_IDENTITY_SECRET (I will set it; it comes from Aimdoc -> Integrations -> Identity Verification). Never hard-code or log it.
- JWT header typ is "aimdoc-identity-assertion+jwt" (live sessions) or "aimdoc-service-assertion+jwt" (background service runs). Route a bearer token to this verifier when it is a three-segment JWT with one of those typ values; otherwise fall through to our existing auth.

Claims to verify
- iss must equal "aimdoc".
- aud must equal exactly "${url}" (the URL Aimdoc has for this server).
- exp / iat: tokens live for minutes and are minted per call; reject expired ones.
- identity_tier: "verified", "email_reply", or "anonymous". Treat any unknown value as "anonymous".
- sub: our own external user id, the one we passed to Aimdoc's identify() for that user. Present only for "verified" and "email_reply" tiers.
- email: normalized email, only alongside sub.
- account: {external_id, domain, name} when the user is linked to one of our accounts.
- context: "service_run" when the call is a delegated background Services execution rather than a live session; service: {key, service_id, version_id?, automation_id?, run_id?} accompanies it for auditability.
- org, agent_id, conversation_id: Aimdoc-side ids, useful for logging only. Ignore unknown claims.

Required behavior
1. initialize and tools/list must succeed for ANY validly signed assertion, including identity_tier "anonymous". Aimdoc discovers our tools with an anonymous token.
2. For tools that read or change customer data: require identity_tier == "verified" (you may also accept "email_reply" for read-only tools), resolve sub to our user, and execute with exactly that user's permissions. Never trust sub from an anonymous tier.
3. Reject a missing or invalid token with HTTP 401 and a clear error; do not fall back to a default user.
4. Log the tier, sub, and context for each call so we can audit service runs.

Deliverables
- The verifier, wired into the server's auth middleware, with the routing rule above so existing auth keeps working.
- Unit tests that mint tokens with a test secret and cover: valid verified token, anonymous token allowed for tools/list but rejected for a user-scoped tool, wrong audience, expired token, wrong issuer, tampered signature.
- A short note in the README on setting AIMDOC_IDENTITY_SECRET.
Keep the change small and idiomatic for this codebase. Ask me before adding new dependencies.`;
}
